import { tx } from '@transifex/native'

/* -------------------------------------------------------------------------
 * The viewer's decimal separator — and the ONE deliberate behaviour
 * divergence between the two halves of the `../InputNumber` seam.
 *
 * ===========================================================================
 * READ THIS BEFORE "FIXING" EITHER HALF
 * ===========================================================================
 *
 * THE ANTD HALF PARSES A TYPED COMMA WRONG, ON PURPOSE, AND IT SHIPS THAT WAY.
 *
 * antd 5.15.4 / rc-input-number 9.0.0 parse the box's text with
 *
 *     str.replace(/[^\w.-]+/g, '')
 *
 * unless the call site passes `decimalSeparator` itself. A comma is not
 * `\w`, `.` or `-`, so the comma is DELETED rather than read as a decimal
 * point. On a Danish or Finnish tenant — where EUR and DKK amounts are
 * written `375,50` — a typed `375,50` therefore commits **37550**: a 100x
 * error, on price and quantity fields, silently. Measured, by render, not
 * read off a doc:
 *
 *     typed "375,50"  no decimalSeparator  -> onChange 37550
 *     typed "375,50"  decimalSeparator=","  -> onChange 375.5
 *
 * That is TODAY's behaviour at 181 of this app's 185 InputNumber tags (only
 * four pass `decimalSeparator` themselves), and it stays exactly that way on
 * the antd branch. THIS IS A CAPTAIN'S DECISION, NOT AN OVERSIGHT: the
 * option of fixing both halves was put to him and he chose to fix the shadcn
 * half only, so that the antd rendering every customer sees today stays
 * byte-for-byte and behaviour-for-behaviour identical while the design switch
 * is in progress. It resolves for everyone when the switch completes and the
 * antd branch is deleted — not before.
 *
 * So: the two halves of this seam PARSE MONEY DIFFERENTLY on a comma locale.
 * Do not "align" them by removing the default below, and do not re-raise the
 * question of fixing the antd half — it has been answered. Removing this
 * default would re-introduce the 100x error into the new design; copying it
 * onto the antd branch would change what shipped customers see, which is the
 * one thing this whole seam exists to prevent. Same shape as `../Spin`'s
 * `delay` default, which is likewise shadcn-only and likewise deliberate.
 *
 * ---------------------------------------------------------------------------
 * HOW THE LOCALE IS RESOLVED
 *
 * From the tx (UI) locale, via `Intl` — the same resolution
 * `src/utils/currency.ts` and `src/modules/hauler-mode/ordrer/format.ts` use,
 * transcribed rather than imported so this seam leaf does not depend on a
 * feature module. Derived rather than hard-coded so it stays right for every
 * locale the platform grows.
 *
 * ONE KNOWN NARROWER CASE, named rather than hidden: when the hauler module
 * is on, `currency.ts` PINS the display of EUR amounts to `fi-FI` regardless
 * of the UI locale. A viewer whose UI is English on such a company therefore
 * reads amounts with a comma and types them with a dot. Aligning input with
 * that pin needs the company's currency, which is company data this leaf has
 * no business reading; a call site that needs it passes `decimalSeparator`
 * explicitly, exactly as the four existing ones already do.
 * ------------------------------------------------------------------------- */

/** The tx (UI) locale as a BCP-47 tag, `en-GB` when tx has none yet. */
const viewerLocale = (): string => {
  const locale = tx.getCurrentLocale()
  return locale?.replaceAll('_', '-') || 'en-GB'
}

/**
 * Resolved separators, per locale, for the lifetime of the module.
 *
 * This is called in the RENDER BODY of every number field — per keystroke,
 * per field, and once per row on a grid that puts an InputNumber in a cell —
 * and constructing an `Intl.NumberFormat` is one of the more expensive Intl
 * operations there is. The locale it is keyed on changes at most on a
 * language switch, so the answer is cacheable outright. Caching only; the
 * resolution below and its `'.'` fallback are unchanged.
 */
const separatorByLocale = new Map<string, string>()

/**
 * `','` on da/fi/de…, `'.'` on en. Falls back to `'.'` if the runtime gives
 * no decimal part at all, so a broken `Intl` cannot make the field unusable.
 *
 * Note what setting this does NOT cost: with a separator set, the parser
 * replaces it with `'.'` and then still strips everything outside `[\w.-]`,
 * so a typed DOT keeps working on a comma locale (`375.50` -> 375.5) and a
 * space thousands separator is still dropped (`1 234,56` -> 1234.56). The
 * separator widens what the field accepts; it narrows nothing.
 */
export const viewerDecimalSeparator = (): string => {
  const locale = viewerLocale()
  const cached = separatorByLocale.get(locale)
  if (cached !== undefined) return cached

  let separator = '.'
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1.1)
    separator = parts.find((part) => part.type === 'decimal')?.value || '.'
  } catch {
    separator = '.'
  }
  separatorByLocale.set(locale, separator)
  return separator
}
